import React, { useEffect, useState } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useConfig } from "../lib/ConfigContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Check, Crown, Sparkles, Zap, Lock, X as XIcon, Quote } from "lucide-react";
import { toast } from "sonner";
import { useRazorpay } from "react-razorpay";

const PLAN_VISUAL = {
  normal: { icon: <Zap size={18} />, accent: "border-[#E4E4E7]", btn: "bg-[#09090B] text-white hover:bg-black", color: "text-[#52525B]" },
  prime: { icon: <Sparkles size={18} />, accent: "border-[#FDE68A] bg-[#FFFBEB]", btn: "bg-[#92400E] text-white hover:bg-[#78350F]", color: "text-[#92400E]" },
  premium: { icon: <Crown size={18} />, accent: "border-[#0055FF] bg-[#EEF4FF] ring-2 ring-[#0055FF]/20", btn: "btn-brand", color: "text-[#0055FF]" },
};

// Derive validity label from either duration_months (legacy) or duration_days (admin_plans SSOT shape)
const VALIDITY_LABEL = (p) => {
  const months = p.duration_months || (p.duration_days ? Math.round(p.duration_days / 30) : null);
  if (!months) return "—";
  return months >= 12 ? "1 year" : months > 1 ? `${months} months` : `${months} month`;
};

export default function Pricing() {
  const { user, tier } = useAuth();
  const { plans: cfgPlans, settings, loading: cfgLoading } = useConfig();
  const nav = useNavigate();
  const [busyPlan, setBusyPlan] = useState(null);
  const { Razorpay, isLoaded } = useRazorpay();

  // Preview Mode = user not logged in. Show plan structure (name, tag, purpose,
  // benefits, limitations) but blur price/duration and replace CTA with
  // "Login to Unlock". Toggleable for admin preview/QA via ?preview=1.
  const previewMode = !user;

  // SSOT: client plans come from ConfigContext (server-filtered by viewer role).
  // Plans shape from /api/config carries either `price_inr` (admin_plans) or legacy `price`.
  const rawPlans = cfgPlans?.client || [];
  const plans = rawPlans.map((p) => ({
    ...p,
    // Normalize for display — id may be "client_normal"/"client_prime"/"client_premium"
    id: p.legacy_id || p.id,
    price: p.price_inr ?? p.price ?? 0,
  }));

  useEffect(() => {
    if (cfgLoading || !user) return;
    if (user.role === "dealer" || user.role === "builder") {
      nav("/dealer?tab=plan", { replace: true });
    }
  }, [user, nav, cfgLoading]);

  const buy = async (planId) => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent("/pricing")}`);
      return;
    }
    setBusyPlan(planId);
    try {
      const order = await api.post("/subscriptions/create-order", { plan_id: planId });
      if (order.data.demo) {
        // Backend says demo (PAYMENTS_DEMO_MODE=true OR Razorpay keys missing).
        // We DO NOT silently activate the plan — that would let users get paid
        // tiers for free. Surface a clear error and stop.
        toast.error("Payment gateway is not configured. Please contact support — your plan was NOT activated.");
        setBusyPlan(null);
        return;
      }
      const options = {
        key: order.data.razorpay_key_id,
        amount: order.data.amount,
        currency: order.data.currency,
        order_id: order.data.order_id,
        name: "HouseEntry Solutions",
        description: `${order.data.plan.name} subscription`,
        prefill: { name: user.name, email: user.email },
        theme: { color: "#0055FF" },
        handler: async (rzp) => {
          try {
            await api.post("/subscriptions/verify", {
              plan_id: planId,
              razorpay_order_id: rzp.razorpay_order_id,
              razorpay_payment_id: rzp.razorpay_payment_id,
              razorpay_signature: rzp.razorpay_signature,
            });
            await refreshUser();
            toast.success("Payment successful — subscription activated!");
            nav("/");
          } catch (err) {
            toast.error(apiErrorMsg(err, "Verification failed"));
          }
        },
      };
      // SDK readiness — prefer the global script (loaded in index.html).
      // react-razorpay's `isLoaded` flips true after its own injection, but the
      // global is usually ready first.
      const RZP = (typeof window !== "undefined" && window.Razorpay) || (isLoaded && Razorpay);
      if (!RZP) {
        toast.error("Payment SDK still loading — please retry in a moment");
        setBusyPlan(null);
        return;
      }
      const rzpInst = new RZP(options);
      rzpInst.open();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Failed to create order"));
    }
    setBusyPlan(null);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] overflow-x-hidden">
      <div className="w-full max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">

        {previewMode && (
          <div className="bg-[#EEF4FF] border border-[#C7D9FF] rounded-sm p-4 text-center mb-8 flex items-center justify-center gap-2 flex-wrap" data-testid="preview-mode-banner">
            <Lock size={14} className="text-[#0055FF]" />
            <span className="text-[13px] text-[#0044CC] font-semibold">Pricing &amp; full access visible after login</span>
            <Button
              size="sm"
              onClick={() => nav("/access?next=/pricing")}
              className="btn-brand rounded-sm h-8 ml-2"
              data-testid="preview-banner-login-cta"
            >
              Sign in to unlock →
            </Button>
          </div>
        )}

        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0055FF] mb-3">
            ◉ Subscription Plans
          </div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-[#09090B] leading-tight">
            Pick a plan that closes more deals
          </h1>
          <p className="text-[15px] text-[#52525B] mt-4">
            Each plan is a complete property-buying decision system — not just listing access.
          </p>
        </div>

        {!settings.plans_enabled && (
          <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-sm p-6 text-center mb-8" data-testid="plans-disabled-banner">
            <div className="font-heading font-semibold text-[#92400E]">Subscription plans are temporarily disabled.</div>
            <p className="text-[13px] text-[#92400E]/80 mt-1">Browsing remains free for everyone — admin will re-enable plans soon.</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 items-stretch">
          {plans.map((p) => {
            const visual = PLAN_VISUAL[p.id] || PLAN_VISUAL.normal;
            const owned = tier === p.id;
            // v2.33 — show downgrade lock UI if user has a higher-priced active plan
            const userPlan = !previewMode && tier ? plans.find((x) => x.id === tier) : null;
            const isDowngrade = !!(userPlan && (p.price || 0) < (userPlan.price || 0));
            // Preview mode: highlight middle plan (Prime). Logged-in: keep existing accent only.
            const isHighlighted = previewMode && p.id === "prime";
            return (
              <div
                key={p.id}
                className={`bg-white border rounded-sm p-5 sm:p-6 lg:p-7 flex flex-col relative w-full min-w-0 h-full ${visual.accent} ${owned ? "ring-2 ring-[#16A34A]" : ""} ${isHighlighted ? "lg:scale-[1.02] lg:shadow-[0_8px_24px_rgba(146,64,14,0.12)] lg:z-10 origin-center" : ""}`}
                data-testid={`plan-card-${p.id}`}
              >
                {isHighlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#92400E] text-white text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-sm shadow-md" data-testid={`plan-popular-${p.id}`}>
                    Most Popular
                  </span>
                )}
                <div className="flex items-center justify-between mb-3">
                  <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2 py-1 border rounded-sm ${
                    p.id === "premium" ? "border-[#0055FF] text-[#0055FF]" :
                    p.id === "prime" ? "border-[#92400E] text-[#92400E]" : "border-[#52525B] text-[#52525B]"
                  }`}>
                    {visual.icon} {p.name}
                  </div>
                  {owned && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-[#16A34A] flex items-center gap-1">
                      <Check size={11} /> Active
                    </span>
                  )}
                </div>

                {p.tagline && (
                  <div className="text-[12px] text-[#52525B] italic mb-3" data-testid={`plan-tagline-${p.id}`}>
                    {p.tagline}
                  </div>
                )}

                {/* Price area — blurred + locked CTA in preview mode */}
                {previewMode ? (
                  <div className="relative mb-4 mt-1" data-testid={`plan-price-locked-${p.id}`}>
                    <div className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-[#09090B] font-mono-data blur-[6px] select-none pointer-events-none" aria-hidden="true">
                      ₹•••
                    </div>
                    <div className="text-[12px] text-[#52525B] blur-[4px] select-none pointer-events-none mb-2" aria-hidden="true">/ ••• months</div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                      <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0055FF] bg-white/95 backdrop-blur px-3 py-1.5 rounded-sm border border-[#C7D9FF] shadow-sm">
                        <Lock size={12} /> Login to View Price
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-[#09090B] font-mono-data break-words">
                      ₹{(p.price || 0).toLocaleString("en-IN")}
                    </div>
                    <div className="text-[12px] text-[#52525B] mb-2">
                      / <span data-testid={`plan-validity-${p.id}`}>{VALIDITY_LABEL(p)}</span>
                    </div>
                  </>
                )}

                {p.purpose && (
                  <div className="text-[12px] text-[#09090B] bg-white/70 border border-[#E4E4E7] rounded-sm p-2.5 mb-4 mt-1" data-testid={`plan-purpose-${p.id}`}>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#A1A1AA]">Purpose</div>
                    {p.purpose}
                  </div>
                )}

                {/* v2.71.11 — Schema-driven sections (preferred). Falls back
                    to legacy benefits/limitations arrays when sections is empty. */}
                {(p.sections && p.sections.length > 0) ? (
                  <div className="flex-1 space-y-4" data-testid={`plan-sections-${p.id}`}>
                    {p.sections.map((s, sIdx) => {
                      const isLimit = s.type === "limitations";
                      return (
                        <div key={s.id} className={sIdx > 0 ? "pt-3 border-t border-[#E4E4E7]" : ""}>
                          <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${isLimit ? "text-[#A1A1AA]" : "text-[#52525B]"}`}>
                            {s.title}
                          </div>
                          <ul className="space-y-1.5" data-testid={`plan-section-${s.id}`}>
                            {(s.items || []).map((it) => (
                              <li key={it.id} className={`flex items-start gap-2 ${isLimit ? "text-[12px] text-[#52525B]" : "text-[13px] text-[#09090B]"}`}>
                                {it.icon ? (
                                  <span className="text-[14px] flex-shrink-0 leading-tight">{it.icon}</span>
                                ) : isLimit ? (
                                  <XIcon size={12} className="text-[#A1A1AA] mt-0.5 flex-shrink-0" />
                                ) : (
                                  <Check size={14} className="text-[#16A34A] mt-0.5 flex-shrink-0" />
                                )}
                                <span>{it.text}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    {p.benefits && p.benefits.length > 0 && (
                      <>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1.5">What you get</div>
                        <ul className="space-y-1.5 flex-1" data-testid={`plan-benefits-${p.id}`}>
                          {p.benefits.map((f, i) => (
                            <li key={`${p.id}-b-${i}`} className="text-[13px] text-[#09090B] flex items-start gap-2">
                              <Check size={14} className="text-[#16A34A] mt-0.5 flex-shrink-0" /> {f}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {p.limitations && p.limitations.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-[#E4E4E7]" data-testid={`plan-limits-${p.id}`}>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#A1A1AA] mb-1.5">Limitations</div>
                        <ul className="space-y-1">
                          {p.limitations.map((f, i) => (
                            <li key={`${p.id}-l-${i}`} className="text-[12px] text-[#52525B] flex items-start gap-2">
                              <XIcon size={12} className="text-[#A1A1AA] mt-0.5 flex-shrink-0" /> {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}

                {p.pitch && (
                  <div className="mt-4 p-3 bg-[#FAFAFA] border-l-2 border-[#0055FF] text-[12px] text-[#52525B] italic flex gap-2" data-testid={`plan-pitch-${p.id}`}>
                    <Quote size={12} className="text-[#0055FF] mt-0.5 flex-shrink-0" />
                    <span>{p.pitch}</span>
                  </div>
                )}

                {previewMode ? (
                  <Button
                    onClick={() => nav("/access?next=/pricing")}
                    className={`mt-6 rounded-sm ${visual.btn} min-h-[48px] font-semibold inline-flex items-center justify-center gap-1.5`}
                    data-testid={`unlock-plan-${p.id}`}
                  >
                    <Lock size={14} /> Login to Unlock
                  </Button>
                ) : (
                  <Button
                    disabled={busyPlan === p.id || owned || isDowngrade}
                    onClick={() => buy(p.id)}
                    className={`mt-6 rounded-sm ${visual.btn} min-h-[48px]`}
                    data-testid={`buy-plan-${p.id}`}
                    title={isDowngrade ? "You're already on a higher plan — upgrades only" : ""}
                  >
                    {owned ? "Current Plan" :
                      isDowngrade ? "Lower than current — locked" :
                      busyPlan === p.id ? "Processing…" :
                      `Buy ${p.name}`}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center text-[12px] text-[#A1A1AA] flex items-center justify-center gap-2" data-testid="payment-mode-note">
          <Lock size={11} /> Secure payments via Razorpay · {previewMode ? "Sign in to see exact pricing" : "Validity per plan"}
        </div>
      </div>
    </div>
  );
}
